import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import styled from "styled-components";
import { RotatingLines } from "react-loader-spinner";
import BottomModalV2, { RefProps } from "../common/BottomModalV2";
import Box from "../common/Box";
import Button from "../common/Button";
import { useExcelUploadMutation } from "../../api/noticeAPI";
import {
  EXCEL_UNMATCHED_REASON_LABEL,
  EXCEL_UPLOAD_GUIDE,
  EXCEL_UPLOAD_MAX_SIZE,
  EXCEL_UPLOAD_SIZE_MESSAGE,
  EXCEL_UPLOAD_TYPE_MESSAGE,
  isExcelUploadFile,
} from "../../constants/notice";
import { getUploadErrorMessage } from "../../utils/excelUploadErrorMessage";

interface IExcelStoreUploadModalProps {
  // 매칭된 매장코드(userId) 목록을 부모에 전달. 기존 선택과의 병합·중복 제거는 부모(fulfillSelectList)가 담당.
  onApply: (matchedStoreIds: string[]) => void;
}

const ExcelStoreUploadModal = (props: IExcelStoreUploadModalProps, ref: React.Ref<RefProps>) => {
  const modalRef = useRef<RefProps>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ExcelUploadResult | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [excelUpload, { isLoading }] = useExcelUploadMutation();

  // 모달 상태 초기화 (열기/다른 파일 선택 시 공통)
  // 닫을 때는 호출하지 않는다. BottomModalV2 가 닫힘 애니메이션(400ms) 동안 children 을 계속 그리므로,
  // 닫는 시점에 초기화하면 결과 화면이 업로드 폼으로 순간 되돌아가는 깜빡임이 생긴다. 다음 modalOpen 에서 초기화된다.
  const resetState = () => {
    setFile(null);
    setResult(null);
    setErrorMessage("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useImperativeHandle(ref, () => ({
    modalOpen: () => {
      resetState();
      modalRef.current?.modalOpen();
    },
    // 닫을 때는 초기화하지 않는다(resetState 주석 참고). 다음 modalOpen 이 초기화를 담당.
    modalClose: () => modalRef.current?.modalClose(),
  }));

  const handleClose = () => {
    if (isLoading) return false; // 업로드 중에는 닫기 방지
    return true;
  };

  const openFilePicker = () => {
    if (isLoading) return;
    fileInputRef.current?.click();
  };

  // 파일 선택 - 전송 전 확장자·5MB 사전 검증 (accept 는 선택창 필터일 뿐 강제력이 없고, 400/413 문구를 프론트가 이미 들고 있으므로 네트워크 낭비 방지)
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    // 같은 파일 재선택 시 onChange 가 다시 동작하도록 value 초기화
    e.target.value = "";
    if (!selected) return;
    if (!isExcelUploadFile(selected.name)) {
      setFile(null);
      setErrorMessage(EXCEL_UPLOAD_TYPE_MESSAGE);
      return;
    }
    if (selected.size > EXCEL_UPLOAD_MAX_SIZE) {
      setFile(null);
      setErrorMessage(EXCEL_UPLOAD_SIZE_MESSAGE);
      return;
    }
    setFile(selected);
    setErrorMessage("");
  };

  // 업로드 - 파싱·매칭은 서버가 수행, 프론트는 파일을 그대로 전송
  const handleUpload = async () => {
    if (isLoading) return;
    if (!file) {
      setErrorMessage("업로드할 .xlsx 파일을 선택해주세요.");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    try {
      const data = await excelUpload(formData).unwrap();
      setResult(data);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(getUploadErrorMessage(error));
    }
  };

  // 선택에 반영 - 매칭된 userId 목록만 부모로 전달
  const handleConfirm = () => {
    if (!result || result.matched.length === 0) return;
    props.onApply(result.matched.map((store) => store.userId));
  };

  const hasMatched = !!result && result.matched.length > 0;

  return (
    <BottomModalV2 ref={modalRef} modalTitle="엑셀로 이행매장 선택" footerBtn={hasMatched ? "선택에 반영" : undefined} onClose={handleClose} onConfirm={handleConfirm}>
      {result === null ? (
        // ====================== 업로드 폼 ======================
        <Box pd="20px 15px">
          <GuideBox>
            <p className="guide-title">엑셀 인식 규칙</p>
            <ul>
              {EXCEL_UPLOAD_GUIDE.map((guide, idx) => (
                <li key={idx}>{guide}</li>
              ))}
            </ul>
          </GuideBox>

          <FileRow>
            <input ref={fileInputRef} type="file" accept=".xlsx" onChange={handleFileChange} />
            <Button variant="outlined" br="round" size="medium" className="file-btn" onClick={openFilePicker}>
              파일 선택
            </Button>
            <span className={file ? "file-name" : "file-name empty"}>{file ? file.name : "선택된 파일 없음"}</span>
          </FileRow>

          {errorMessage && <ErrorText>{errorMessage}</ErrorText>}

          <UploadBtnBox>
            {isLoading ? (
              <div className="loading">
                <RotatingLines width="28" strokeColor="#00263e" strokeWidth="3" />
                <span>업로드 중입니다...</span>
              </div>
            ) : (
              <Button variant="contained" br="round" onClick={handleUpload} disabled={!file}>
                업로드
              </Button>
            )}
          </UploadBtnBox>
        </Box>
      ) : (
        // ====================== 업로드 결과 ======================
        <Box pd="20px 15px">
          <SummaryBox>
            <li>
              <span className="label">전체</span>
              <span className="count">{result.summary.total}</span>
            </li>
            <li className="matched">
              <span className="label">성공</span>
              <span className="count">{result.summary.matched}</span>
            </li>
            <li className="unmatched">
              <span className="label">실패</span>
              <span className="count">{result.summary.unmatched}</span>
            </li>
          </SummaryBox>

          {result.unmatched.length > 0 ? (
            <FailTable>
              <thead>
                <tr>
                  <th className="row">행</th>
                  <th>입력값</th>
                  <th>사유</th>
                </tr>
              </thead>
              <tbody>
                {result.unmatched.map((item, idx) => (
                  <tr key={idx}>
                    <td className="row">{item.row}</td>
                    <td className="value">{item.value}</td>
                    <td>{EXCEL_UNMATCHED_REASON_LABEL[item.reason] ?? item.reason}</td>
                  </tr>
                ))}
              </tbody>
            </FailTable>
          ) : (
            <ResultMessage>모든 행이 매장과 매칭되었습니다.</ResultMessage>
          )}

          {!hasMatched && <ResultMessage className="warning">선택에 반영할 매장이 없습니다. 파일을 확인 후 다시 업로드해주세요.</ResultMessage>}

          <ReselectBox>
            <Button variant="text" className="reselect" onClick={resetState}>
              다른 파일 선택
            </Button>
          </ReselectBox>
        </Box>
      )}
    </BottomModalV2>
  );
};

const GuideBox = styled.div`
  padding: 12px 14px;
  margin-bottom: 16px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.color.bgPrimary};
  .guide-title {
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 700;
    color: ${({ theme }) => theme.color.primary};
  }
  > ul > li {
    position: relative;
    padding-left: 10px;
    font-size: 12px;
    line-height: 18px;
    color: ${({ theme }) => theme.color.secondary};
    word-break: keep-all;
    &::before {
      content: "";
      position: absolute;
      top: 8px;
      left: 0;
      width: 3px;
      height: 3px;
      border-radius: 50%;
      background-color: ${({ theme }) => theme.color.tertiary};
    }
  }
`;

const FileRow = styled.div`
  ${({ theme }) => theme.flexBox.flexMixin("row", "flex-start")}
  > input[type="file"] {
    display: none;
  }
  .file-btn {
    flex: 0 0 auto;
    width: auto;
  }
  .file-name {
    flex: 1 1 auto;
    margin-left: 10px;
    ${({ theme }) => theme.textOverflow.singleLine()}
    font-size: 14px;
    color: ${({ theme }) => theme.color.primary};
    &.empty {
      color: ${({ theme }) => theme.color.tertiary};
    }
  }
`;

const ErrorText = styled.p`
  margin-top: 10px;
  font-size: 13px;
  line-height: 18px;
  color: ${({ theme }) => theme.color.warning};
  word-break: keep-all;
`;

const UploadBtnBox = styled.div`
  margin-top: 20px;
  .loading {
    ${({ theme }) => theme.flexBox.flexMixin("row", "center")}
    height: 40px;
    > span {
      margin-left: 10px;
      font-size: 14px;
      color: ${({ theme }) => theme.color.secondary};
    }
  }
`;

const SummaryBox = styled.ul`
  ${({ theme }) => theme.flexBox.flexMixin("row", "space-around", "stretch")}
  padding: 14px 0;
  margin-bottom: 16px;
  border: 1px solid ${({ theme }) => theme.color.linePrimary};
  border-radius: 8px;
  > li {
    flex: 1 1 0;
    ${({ theme }) => theme.flexBox.flexMixin("column", "center")}
    &:not(:first-child) {
      border-left: 1px solid ${({ theme }) => theme.color.linePrimary};
    }
    .label {
      font-size: 12px;
      color: ${({ theme }) => theme.color.tertiary};
    }
    .count {
      margin-top: 4px;
      font-family: "Poppins";
      font-size: 22px;
      color: ${({ theme }) => theme.color.primary};
    }
    &.matched .count {
      color: ${({ theme }) => theme.color.subMain};
    }
    &.unmatched .count {
      color: ${({ theme }) => theme.color.warning};
    }
  }
`;

const FailTable = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 13px;
  th,
  td {
    padding: 10px 6px;
    border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};
    text-align: left;
    vertical-align: top;
    line-height: 18px;
    word-break: break-all;
  }
  th {
    background-color: ${({ theme }) => theme.color.bgPrimary};
    font-weight: 500;
    color: ${({ theme }) => theme.color.secondary};
  }
  .row {
    width: 40px;
    text-align: center;
  }
  td.row {
    font-family: "Poppins";
    color: ${({ theme }) => theme.color.primary};
  }
  td.value {
    color: ${({ theme }) => theme.color.primary};
  }
  td:last-child {
    color: ${({ theme }) => theme.color.warning};
  }
`;

const ResultMessage = styled.p`
  padding: 16px 0;
  text-align: center;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.color.tertiary};
  word-break: keep-all;
  &.warning {
    color: ${({ theme }) => theme.color.warning};
  }
`;

const ReselectBox = styled.div`
  margin-top: 8px;
  text-align: center;
  .reselect {
    font-family: inherit;
    font-size: 14px;
    text-decoration: underline;
  }
`;

export default forwardRef(ExcelStoreUploadModal);
